'use client';

import { useBunja } from 'bunja/react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';

import { SegmentPage } from '@flipster/features/src/misc/segment/segment-page';
import { useReferralInfo } from '@flipster/features/src/shared/referral-code/state';
import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { FormattedMessage } from '@flipster/i18n';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { Button } from '@flipster/ui';
import segment, { ReferralEvent } from '@flipster/utils/segment';

import { ContinueWithApple } from '@web/app/[locale]/(web)/_auth/continue-with-apple';
import { ContinueWithGoogle } from '@web/app/[locale]/(web)/_auth/continue-with-google';
import { ContinueWithPasskey } from '@web/app/[locale]/(web)/_auth/continue-with-passkey';
import Captcha from '@web/components/Captcha';
import { useBlockedRegion } from '@web/hooks/useBlockedRegion';

import { passkeyLoginFlowBunja } from '../../_passkey/bunja';
import { restrictedMessage } from '../_constants';
import { h1 } from '../_style';

const Page = () => {
  const referralInfo = useReferralInfo();
  const t = useTranslations();
  const router = useRouter();
  const { openPasskeyLoginModal } = useBunja(passkeyLoginFlowBunja);

  const isSpotOnly = useCheckSpotOnly();
  const { isRestrictedRegion } = useBlockedRegion();

  const onClickEmailSignUp = () => {
    segment.track(ReferralEvent.SignUpEmailClick, referralInfo);
    router.push('/email-signup');
  };

  const onClickLogIn = () => {
    segment.track(ReferralEvent.SignInEmailClick, referralInfo);
    router.push('/login');
  };

  const onClickContinueWithGoogle = () => {
    segment.track(ReferralEvent.SignInSsoClick, { auth_type: 'google', ...referralInfo });
  };

  const onClickContinueWithApple = () => {
    segment.track(ReferralEvent.SignInSsoClick, { auth_type: 'apple', ...referralInfo });
  };

  const title = useMemo(() => {
    if (isSpotOnly) {
      return (
        <>
          <p>
            <FormattedMessage id='Autogen_common.r251126.s_Crypto_Made' />
          </p>
          <p className={css({ color: 'content.accent.info' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Simple' />
          </p>
        </>
      );
    }
    return (
      <>
        <p>
          <FormattedMessage id='Autogen_common.r251126.s_Zero_Friction' />
        </p>
        <p className={css({ color: 'content.accent.info' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Perps' />
        </p>
      </>
    );
  }, [isSpotOnly]);

  return (
    <>
      <SegmentPage name='sign_in_page_view' />
      <div
        className={css({
          maxW: { base: 480, tablet: 412 },
          py: { base: 32, tablet: 120 },
          px: { base: 16, tablet: 0 },
          my: 0,
          mx: 'auto',
        })}
      >
        <header className={flex({ justify: 'between', mb: 40 })}>
          <h1 className={h1}>{title}</h1>
        </header>
        <div className={css({ '& > * + *': { mt: 8 } })}>
          <Button shape='primary' onClick={onClickEmailSignUp} isFull>
            <FormattedMessage id='Autogen_common.r251126.s_Sign_up_with_Email' />
          </Button>
          <ContinueWithPasskey
            label={t('Autogen_common.r251126.s_Continue_with_Passkey')}
            onClick={openPasskeyLoginModal}
          />
          <ContinueWithGoogle
            label={t('Autogen_common.r251126.s_Continue_with_Google')}
            onClick={onClickContinueWithGoogle}
          />
          <ContinueWithApple
            label={t('Autogen_common.r251126.s_Continue_with_Apple')}
            onClick={onClickContinueWithApple}
          />
        </div>
        <p
          className={css({ my: 24, textStyle: 'Text/M/Strong', color: 'content.default.level-3', textAlign: 'center' })}
        >
          OR
        </p>
        <div>
          <Button shape='secondary' onClick={onClickLogIn} isFull>
            <FormattedMessage id='Autogen_common.r251126.s_Log_in_with_email' />
          </Button>
        </div>
        {isRestrictedRegion && (
          <p className={css({ pt: 16, textStyle: 'Text/S/Regular', color: 'content.default.level-5' })}>
            {restrictedMessage}
          </p>
        )}
      </div>
      <Captcha type='Auth' />
    </>
  );
};

export default Page;
